import { useEffect, useRef } from "react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

// Schedules browser notifications + soft in-app toasts at each ritual's remind_at (HH:MM).
// Dedupes per (date, ritual_id) via localStorage. Re-scans every 60s in case new rituals
// are adopted while the app is open. Only fires when the app is open (no service worker).
// Includes a "Snooze 10" action that re-queues the ritual for 10 minutes later via
// a localStorage-backed snooze queue that survives reloads.

const FIRED_KEY = "aging.reminders.fired";
const SNOOZE_KEY = "aging.reminders.snooze"; // { ritualId: { fire_at_ms, snapshot } }
const SNOOZE_MS = 10 * 60 * 1000;
const PROC_PHOTOS_SEEN_KEY = "aging.procphotos.notified"; // set of photo ids already toasted
const NOTIFICATIONS_SEEN_KEY = "aging.notifications.notified"; // ids we've already surfaced a toast for

export const PROC_PHOTOS_EVENT = "aging:proc-photos-changed";

function firedSet() {
  try { return new Set(JSON.parse(localStorage.getItem(FIRED_KEY) || "[]")); }
  catch { return new Set(); }
}
function markFired(key) {
  const s = firedSet();
  s.add(key);
  const arr = Array.from(s).slice(-200);
  localStorage.setItem(FIRED_KEY, JSON.stringify(arr));
}

function readSnooze() {
  try { return JSON.parse(localStorage.getItem(SNOOZE_KEY) || "{}"); }
  catch { return {}; }
}
function writeSnooze(obj) {
  localStorage.setItem(SNOOZE_KEY, JSON.stringify(obj));
}

function todayKey(id) {
  const d = new Date();
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `${ymd}::${id}`;
}

function minutesFromHHMM(hhmm) {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm || "");
  if (!m) return null;
  return parseInt(m[1]) * 60 + parseInt(m[2]);
}

function nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

// Returns true if `now` is inside a quiet window. Handles wrap-around (e.g. 22:00–07:00).
function inQuietHours(now, profile) {
  if (!profile || !profile.quiet_hours_enabled) return false;
  const s = minutesFromHHMM(profile.quiet_hours_start);
  const e = minutesFromHHMM(profile.quiet_hours_end);
  if (s === null || e === null) return false;
  if (s === e) return false; // zero-length
  if (s < e) return now >= s && now < e;
  return now >= s || now < e; // wraps midnight
}

async function ensurePermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted" || Notification.permission === "denied") return Notification.permission;
  try { return await Notification.requestPermission(); } catch { return "denied"; }
}

function snoozeRitual(ritual) {
  const q = readSnooze();
  q[ritual.id] = {
    fire_at_ms: Date.now() + SNOOZE_MS,
    snapshot: {
      id: ritual.id,
      title: ritual.title,
      description: ritual.description,
      source_spa: ritual.source_spa || null,
    },
  };
  writeSnooze(q);
  toast("Snoozed 10 minutes.", { duration: 3000 });
}

function fireReminder(ritual, permission, opts = {}) {
  const { markToday = true, isSnooze = false } = opts;
  const key = todayKey(ritual.id);
  if (markToday && firedSet().has(key)) return; // dedup per day
  if (markToday) markFired(key);

  const title = ritual.source_spa
    ? `${ritual.title} · ${ritual.source_spa}`
    : `Ritual · ${ritual.title}`;
  const body = ritual.description || "A quiet moment. It's time.";
  const prefix = isSnooze ? "Snooze over — " : "";

  if (permission === "granted") {
    try {
      const n = new Notification(prefix + title, { body, tag: `ritual-${ritual.id}` });
      n.onclick = () => { window.focus(); window.location.href = "/rituals"; };
    } catch { /* ignore */ }
  }

  toast(prefix + title, {
    description: body,
    duration: 12000,
    action: {
      label: "Snooze 10",
      onClick: () => snoozeRitual(ritual),
    },
  });
}

function fireProcedureReminder(proc, permission) {
  const key = `${todayKey(proc.id)}::proc`;
  if (firedSet().has(key)) return;
  markFired(key);

  const nice = new Date(proc.next_visit_date).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });
  const title = `Tomorrow · ${proc.name}`;
  const body = `Next visit ${nice}.${proc.notes ? " " + proc.notes : ""}`.trim();

  if (permission === "granted") {
    try {
      const n = new Notification(title, { body, tag: `proc-${proc.id}` });
      n.onclick = () => { window.focus(); window.location.href = "/tracking"; };
    } catch { /* ignore */ }
  }
  toast(title, { description: body, duration: 12000 });
}

function readNotifiedPhotos() {
  try { return new Set(JSON.parse(localStorage.getItem(PROC_PHOTOS_SEEN_KEY) || "[]")); }
  catch { return new Set(); }
}
function markPhotoNotified(id) {
  const s = readNotifiedPhotos();
  s.add(id);
  localStorage.setItem(PROC_PHOTOS_SEEN_KEY, JSON.stringify(Array.from(s).slice(-200)));
}

function readNotifiedNotifs() {
  try { return new Set(JSON.parse(localStorage.getItem(NOTIFICATIONS_SEEN_KEY) || "[]")); }
  catch { return new Set(); }
}
function markNotifNotified(id) {
  const s = readNotifiedNotifs();
  s.add(id);
  localStorage.setItem(NOTIFICATIONS_SEEN_KEY, JSON.stringify(Array.from(s).slice(-200)));
}

function fireNotification(n, permission) {
  const isRecap = n.kind === "weekly_recap";
  const isRestDay = n.kind === "rest-day-signal";
  const title = n.title || "Celebrating You";
  const body = n.body || `${n.from_name || "Someone"} is cheering your milestone.`;
  const destination = isRecap || isRestDay ? "/tracking" : "/community";
  const actionLabel = isRecap ? "See recap" : isRestDay ? "Open Fitness" : "See";
  if (permission === "granted") {
    try {
      const notif = new Notification(title, { body, tag: `notif-${n.id}` });
      notif.onclick = () => { window.focus(); window.location.href = destination; };
    } catch { /* ignore */ }
  }
  toast(title, {
    description: body,
    duration: 14000,
    action: { label: actionLabel, onClick: () => { window.location.href = destination; } },
  });
}

function fireCheerNotification(n, permission) {
  fireNotification(n, permission);
}

function fireProcedurePhotoReminder(photo, permission) {
  const title = `New from ${photo.spa_name}`;
  const body = `Before & after · ${photo.procedure_name}`;
  if (permission === "granted") {
    try {
      const n = new Notification(title, { body, tag: `proc-photo-${photo.id}` });
      n.onclick = () => { window.focus(); window.location.href = "/tracking"; };
    } catch { /* ignore */ }
  }
  toast(title, {
    description: body,
    duration: 12000,
    action: {
      label: "View",
      onClick: () => { window.location.href = "/tracking"; },
    },
  });
}

export default function useReminders() {
  const timerRef = useRef(null);
  const permRef = useRef("default");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      permRef.current = await ensurePermission();
    })();

    async function tick() {
      try {
        // Fetch profile once per tick to honour quiet hours if enabled.
        let profile = null;
        try { profile = (await endpoints.profile()).data; } catch { /* ignore */ }
        const nowMin = nowMinutes();
        const quiet = inQuietHours(nowMin, profile);

        // Process snoozed reminders first (can fire more than once per day)
        const q = readSnooze();
        const now = Date.now();
        let changed = false;
        for (const [rid, entry] of Object.entries(q)) {
          if (entry && entry.fire_at_ms && now >= entry.fire_at_ms) {
            if (quiet) continue; // hold snoozed until quiet hours end
            fireReminder(entry.snapshot, permRef.current, { markToday: false, isSnooze: true });
            delete q[rid];
            changed = true;
          }
        }
        if (changed) writeSnooze(q);

        // Skip scheduled fires when in quiet hours (dedup stays unmarked so they fire once quiet ends)
        if (quiet) {
          if (!cancelled) timerRef.current = setTimeout(tick, 60_000);
          return;
        }

        const { data: rituals } = await endpoints.rituals();
        for (const r of rituals) {
          if (!r.remind_at) continue;
          const t = minutesFromHHMM(r.remind_at);
          if (t === null) continue;
          if (nowMin >= t) {
            fireReminder(r, permRef.current);
          }
        }

        // Procedure reminders: fire when today == next_visit_date - 1 day
        try {
          const { data: procs } = await endpoints.procedures();
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          const y = tomorrow.getFullYear();
          const m = String(tomorrow.getMonth() + 1).padStart(2, "0");
          const d = String(tomorrow.getDate()).padStart(2, "0");
          const tomorrowIso = `${y}-${m}-${d}`;
          for (const p of procs) {
            if (p.next_visit_date === tomorrowIso) {
              fireProcedureReminder(p, permRef.current);
            }
          }
        } catch { /* silent */ }

        // New procedure photos from spas
        try {
          const { data: photos } = await endpoints.procedurePhotos();
          const notified = readNotifiedPhotos();
          let anyNew = false;
          for (const ph of photos) {
            if (!ph.seen && !notified.has(ph.id)) {
              fireProcedurePhotoReminder(ph, permRef.current);
              markPhotoNotified(ph.id);
              anyNew = true;
            }
          }
          if (anyNew) {
            try { window.dispatchEvent(new Event(PROC_PHOTOS_EVENT)); } catch { /* ignore */ }
          }
        } catch { /* silent */ }

        // General notifications — currently: cheer arrivals on milestone posts + weekly recap prompt
        try {
          // Best-effort ensure a weekly-recap notification exists on Sunday.
          endpoints.ensureWeeklyRecapNotification().catch(() => {});
          const { data: notifs } = await endpoints.notifications(true);
          const notified = readNotifiedNotifs();
          for (const n of notifs) {
            if (!notified.has(n.id)) {
              fireNotification(n, permRef.current);
              markNotifNotified(n.id);
              endpoints.markNotificationSeen(n.id).catch(() => {});
            }
          }
        } catch { /* silent */ }

        // Tracking log reminders — unified across procedure/fitness/peptide/skincare/habit/custom.
        // For each reminder past its `remind_at`, fire a toast and either advance the
        // schedule (recurring cadences) or delete it (once) so it doesn't re-fire.
        try {
          const { data: trackReminders } = await endpoints.listTrackingReminders();
          const nowMs = Date.now();
          for (const r of trackReminders || []) {
            const due = new Date(r.remind_at).getTime();
            if (isNaN(due) || due > nowMs) continue;
            const label = r.ref_label || r.room;
            fireNotification(
              { id: `track-rem-${r.id}-${r.remind_at}`, title: `Reminder · ${label}`, body: r.note || "A small nudge from your logs." },
              permRef.current,
            );
            if (r.cadence === "once") {
              endpoints.deleteTrackingReminder(r.id).catch(() => {});
            } else {
              const next = new Date(due);
              if (r.cadence === "daily") next.setDate(next.getDate() + 1);
              else if (r.cadence === "weekly") next.setDate(next.getDate() + 7);
              else if (r.cadence === "monthly") next.setMonth(next.getMonth() + 1);
              // Skip forward until we're past "now" so a stale reminder doesn't spam.
              while (next.getTime() <= nowMs) {
                if (r.cadence === "daily") next.setDate(next.getDate() + 1);
                else if (r.cadence === "weekly") next.setDate(next.getDate() + 7);
                else if (r.cadence === "monthly") next.setMonth(next.getMonth() + 1);
                else break;
              }
              const iso = toLocalIso(next);
              endpoints.updateTrackingReminder(r.id, { remind_at: iso }).catch(() => {});
            }
          }
        } catch { /* silent */ }
      } catch { /* silent */ }
      if (!cancelled) timerRef.current = setTimeout(tick, 60_000);
    }

    timerRef.current = setTimeout(tick, 2_000);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
}

/** Format a Date into "YYYY-MM-DDTHH:MM" using local time so the backend
 *  ISO parser (which accepts naive-local ISO) treats it as the member's
 *  wall clock. Avoids TZ drift when advancing recurring reminders. */
function toLocalIso(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

